import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ShoppingCart, Heart, Star, Zap, ShieldCheck, Headphones, Check } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { useCart } from "../contexts/CartContext";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { formatPrice, discountedPrice } from "../lib/api";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data.product);
      setSimilar(r.data.similar);
      setActiveImg(0);
    });
  }, [slug]);

  if (!product) {
    return (
      <div className="gf-container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="aspect-[4/5] rounded-3xl shimmer" />
          <div className="space-y-4">
            <div className="h-10 rounded-xl shimmer w-2/3" />
            <div className="h-6 rounded-xl shimmer w-1/3" />
            <div className="h-32 rounded-xl shimmer" />
          </div>
        </div>
      </div>
    );
  }

  const finalPrice = discountedPrice(product);
  const gallery = product.gallery?.length ? product.gallery : [product.cover_image];
  const inWl = inWishlist(product.id);

  const onAdd = async () => {
    const r = await addToCart(product.id, 1);
    if (r.requireLogin) toast.error(t("common.please_login"));
    else if (r.ok) toast.success(t("common.added"));
  };

  const onBuy = async () => {
    const r = await addToCart(product.id, 1);
    if (r.requireLogin) {
      toast.error(t("common.please_login"));
      navigate("/login?next=/cart");
    } else {
      navigate("/cart");
    }
  };

  return (
    <div className="min-h-screen pb-20" data-testid="product-page">
      <div className="gf-container py-8">
        <nav className="text-sm text-slate-500 mb-6 flex items-center gap-1.5">
          <Link to="/" className="hover:text-[#A855F7]">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to={`/catalog?category=${encodeURIComponent(product.category)}`} className="hover:text-[#A855F7]">{product.category}</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-900 font-medium line-clamp-1">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-[0_24px_60px_rgba(15,15,20,0.08)] aspect-[4/5] relative">
              <img src={gallery[activeImg]} alt={product.title} className="w-full h-full object-cover" />
              {product.discount_percent > 0 && (
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-[#A855F7] text-white text-xs font-bold">
                  -{Math.round(product.discount_percent)}%
                </div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="mt-4 flex gap-3">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      activeImg === i ? "border-[#A855F7] ring-2 ring-[#A855F7]/30" : "border-slate-200"
                    }`}
                  >
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col">
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">{product.title}</h1>
            <div className="text-sm text-slate-500 mt-1">{product.platform}</div>

            {product.rating > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-yellow-400 text-yellow-400" : "text-slate-300"}`} />
                  ))}
                </div>
                <span className="text-sm text-slate-500">({product.rating_count} reviews)</span>
              </div>
            )}

            <div className="flex items-baseline gap-3 mt-6">
              <span className="font-display text-4xl font-bold text-slate-900" data-testid="product-final-price">{formatPrice(finalPrice, lang)}</span>
              {product.discount_percent > 0 && (
                <>
                  <span className="text-lg text-slate-400 line-through">{formatPrice(product.price, lang)}</span>
                  <span className="px-2 py-0.5 rounded-full bg-[#A855F7]/10 text-[#A855F7] text-sm font-semibold">-{Math.round(product.discount_percent)}%</span>
                </>
              )}
            </div>

            <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
              <Check className="w-4 h-4" /> {t("common.in_stock")}
            </div>

            <div className="mt-8 flex gap-3">
              <Button onClick={onBuy} className="btn-neon h-12 px-7 rounded-xl flex-1 font-semibold" data-testid="buy-now-btn">
                <ShoppingCart className="w-4 h-4 mr-2" /> {t("common.buy_now")}
              </Button>
              <Button onClick={onAdd} variant="outline" className="h-12 px-7 rounded-xl font-semibold border-slate-300" data-testid="add-cart-btn">
                {t("common.add_to_cart")}
              </Button>
              <Button
                onClick={() => toggleWishlist(product.id)}
                variant="outline"
                size="icon"
                className={`h-12 w-12 rounded-xl border-slate-300 ${inWl ? "bg-[#A855F7] text-white border-[#A855F7]" : ""}`}
                data-testid="wishlist-toggle"
              >
                <Heart className={`w-4 h-4 ${inWl ? "fill-current" : ""}`} />
              </Button>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: Zap, t: t("product.delivery"), s: t("product.delivery_sub") },
                { icon: ShieldCheck, t: t("product.secure"), s: t("product.secure_sub") },
                { icon: Headphones, t: t("product.support"), s: t("product.support_sub") },
              ].map((it, i) => (
                <div key={i} className="rounded-xl bg-white border border-slate-200 p-4">
                  <it.icon className="w-5 h-5 text-[#A855F7] mb-2" />
                  <div className="font-semibold text-sm text-slate-900">{it.t}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{it.s}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 pt-6 border-t border-slate-200">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">{t("product.category")}</div>
                <div className="font-medium text-slate-900 mt-0.5">{product.category}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">{t("product.platform")}</div>
                <div className="font-medium text-slate-900 mt-0.5">{product.platform || "—"}</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trailer */}
        {product.trailer_url && (
          <div className="mt-12">
            <h3 className="font-display text-xl font-bold text-slate-900 mb-4">Trailer</h3>
            <div className="rounded-3xl overflow-hidden bg-black aspect-video">
              <video src={product.trailer_url} controls className="w-full h-full" />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-16">
          <Tabs defaultValue="desc">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="desc" data-testid="tab-description">{t("product.description")}</TabsTrigger>
              {product.requirements && <TabsTrigger value="reqs" data-testid="tab-requirements">{t("product.requirements")}</TabsTrigger>}
              <TabsTrigger value="reviews" data-testid="tab-reviews">{t("product.reviews")}</TabsTrigger>
            </TabsList>
            <TabsContent value="desc" className="rounded-2xl bg-white border border-slate-200 p-6 mt-4 text-slate-700 leading-relaxed">
              {product.description || product.short_description}
            </TabsContent>
            {product.requirements && (
              <TabsContent value="reqs" className="rounded-2xl bg-white border border-slate-200 p-6 mt-4">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(product.requirements).map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs text-slate-500 uppercase tracking-wide">{k}</dt>
                      <dd className="text-slate-900 font-medium mt-0.5">{v}</dd>
                    </div>
                  ))}
                </dl>
              </TabsContent>
            )}
            <TabsContent value="reviews" className="rounded-2xl bg-white border border-slate-200 p-6 mt-4 text-slate-500 text-sm">
              Be the first to leave a review for this product.
            </TabsContent>
          </Tabs>
        </div>

        {/* Similar */}
        {similar.length > 0 && (
          <div className="mt-16">
            <h3 className="font-display text-2xl font-bold text-slate-900 mb-6">{t("product.similar")}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {similar.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
